import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Container,
  ListItemText,
  Menu,
  MenuItem,
  Button as MuiButton,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Button from "../../../components/CustomButton";
import CustomSelect from "../../../components/Select/Select";
import { AppColors } from "../../../util/AppColors";
import AppointmentsFacilityReport from "./Appointments/AppointmentsFacilityReport";
import AppointmentsOverview from "./Appointments/AppointmentsOverview";
import AppointmentsTableReport from "./Appointments/AppointmentsTableReport";
import AppointmentsTabTableReport from "./Appointments/AppointmentsTabTableReport";
import BillingCollectionsTableReport from "./Billing/BillingCollectionsTableReport";
import BillingInvoicesTableReport from "./Billing/BillingInvoicesTableReport";
import BillingOverview from "./Billing/BillingOverview";
import BillingPurchaseReturnReport from "./Billing/BillingPurchaseReturnReport";
import BillingSalesReturnReport from "./Billing/BillingSalesReturnReport";
import AppointmentsReport from "./Pets/AppointmentsReport";
import ExecutiveOverview from "./Executive/ExecutiveOverview";
import PetDetailsReport from "./Pets/PetDetailsReport";
import PetsOverview from "./Pets/PetsOverview";
import ServiceLevel2Report from "./Service/ServiceLevel2Report";
import ServicesOverview from "./Service/ServicesOverview";
import ServiceTableReport from "./Service/ServiceTableReport";
import StockBatchReport from "./Stock/StockBatchReport";
import StockDumpReport from "./Stock/StockDumpReport";
import StockOverview from "./Stock/StockOverview";
import StockPurchaseReturnReport from "./Stock/StockPurchaseReturnReport";
import StockSalesReturnReport from "./Stock/StockSalesReturnReport";
import VetsOverview from "./Vets/VetsOverview";
import VetsRevenueReport from "./Vets/VetsRevenueReport";
import VetsSlotsReport from "./Vets/VetsSlotsReport";
import VetsSpecialityReport from "./Vets/VetsSpecialityReport";
import VetsUpcomingReport from "./Vets/VetsUpcomingReport";

const tabArray = [
  { name: "Executive", tabs: ["Overview"] },
  {
    name: "Vets",
    tabs: [
      "Overview",
      "Vet Performance",
      "Vet Specialties",
      "Utilization vs Capacity",
      // "Delay",
      "Upcoming Appointments",
    ],
  },
  { name: "Pets", tabs: ["Overview", "Pet Overview", "Appointment History"] },
  {
    name: "Appointments",
    tabs: [
      "Overview",
      "Appointment Trends",
      "Appointment Analytics",
      "Facility Utilization",
    ],
  },
  {
    name: "Billing",
    tabs: [
      "Overview",
      "Revenue Report",
      "Collections Report",
      {
        type: "dropdown",
        label: "Returns Report",
        options: [
          { value: "Sales Return", label: "Sales" },
          { value: "Purchase Return", label: "Purchase" },
        ],
      },
    ],
  },
  {
    name: "Stock",
    tabs: ["Overview", "Stock Overview", "Batch Analysis", "Stock Returns"],
  },
  { name: "Services", tabs: ["Overview", "Service Analytics", "Service Usage"] },
];

export const REPORTS_TAB_PATHS = {
  Executive: "executive",
  Vets: "vets",
  Pets: "pets",
  Appointments: "appointments",
  Billing: "billing",
  Stock: "stock",
  Services: "services",
};

export const REPORTS_PATH_TO_TAB = {
  executive: "Executive",
  vets: "Vets",
  pets: "Pets",
  appointments: "Appointments",
  billing: "Billing",
  stock: "Stock",
  services: "Services",
};

export const REPORTS_SUB_TAB_PATHS = {
  Overview: "overview",
  "Vet Performance": "vet-performance",
  "Vet Specialties": "vet-specialties",
  "Utilization vs Capacity": "utilization-vs-capacity",
  "Upcoming Appointments": "upcoming-appointments",
  "Pet Overview": "pet-overview",
  "Appointment History": "appointment-history",
  "Appointment Trends": "appointment-trends",
  "Appointment Analytics": "appointment-analytics",
  "Facility Utilization": "facility-utilization",
  "Revenue Report": "revenue-report",
  "Collections Report": "collections-report",
  "Sales Return": "sales-return",
  "Purchase Return": "purchase-return",
  "Stock Overview": "stock-overview",
  "Batch Analysis": "batch-analysis",
  "Stock Returns": "stock-returns",
  "Service Analytics": "service-analytics",
  "Service Usage": "service-usage",
};

export const REPORTS_PATH_TO_SUB_TAB = Object.fromEntries(
  Object.entries(REPORTS_SUB_TAB_PATHS).map(([label, slug]) => [slug, label]),
);

const getDefaultSubTab = (groupName) => {
  const group = tabArray.find((g) => g.name === groupName);
  const first = group?.tabs?.[0];
  if (!first) return null;
  if (typeof first === "string") return first;
  return first?.options?.[0]?.value ?? null;
};

export const ReportsRedirect = () => {
  const location = useLocation();
  const tabFromState = location.state?.activeTab ?? location.state?.selectedTab;
  const path = REPORTS_TAB_PATHS[tabFromState] ?? REPORTS_TAB_PATHS.Vets;
  const groupName = REPORTS_PATH_TO_TAB[path];
  const defaultSub = getDefaultSubTab(groupName);
  const subPath = defaultSub ? REPORTS_SUB_TAB_PATHS[defaultSub] : null;
  return (
    <Navigate
      to={subPath ? `/reports/${path}/${subPath}` : `/reports/${path}`}
      replace
      state={location.state}
    />
  );
};

const Reports = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab, subTab: subTabParam } = useParams();
  const activeTab = REPORTS_PATH_TO_TAB[tab];
  const [returnsReportAnchor, setReturnsReportAnchor] = useState(null);
  const [selectedDrop, setSelectedDrop] = useState("Sales");

  const selectedTab = useMemo(() => {
    if (!activeTab) return null;
    return REPORTS_PATH_TO_SUB_TAB[subTabParam] ?? getDefaultSubTab(activeTab);
  }, [activeTab, subTabParam]);

  const navigateToTab = (groupName, subTabName) => {
    const path = REPORTS_TAB_PATHS[groupName];
    if (!path) return;
    const resolvedSub = subTabName ?? getDefaultSubTab(groupName);
    const subPath = resolvedSub ? REPORTS_SUB_TAB_PATHS[resolvedSub] : null;
    navigate(subPath ? `/reports/${path}/${subPath}` : `/reports/${path}`, {
      state: location.state,
    });
  };

  const handleMainTabClick = (groupName) => {
    if (!groupName || groupName === activeTab) return;
    navigateToTab(groupName);
  };

  const handleSubTabClick = (groupName, subTab) => {
    if (!groupName || !subTab) return;
    navigateToTab(groupName, subTab);
  };

  const activeGroup = tabArray?.find((group) => group.name === activeTab);

  if (!activeTab) {
    return (
      <Navigate
        to={`/reports/${REPORTS_TAB_PATHS.Vets}/${REPORTS_SUB_TAB_PATHS["Vet Performance"]}`}
        replace
        state={location.state}
      />
    );
  }

  return (
    <Container maxWidth="xl">
      <div className="back-white ph20 pt10 pb10">
        {/* Primary Tabs - Main Categories */}
        <div
          className="flex-row-ali-cen"
          style={{ flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}
        >
          {tabArray?.map((li, i) => (
            <div key={`${li?.name}_${i}`} style={{ marginRight: "8px" }}>
              <Button
                text={li?.name}
                tabSelectdBtn={activeTab === li?.name}
                textBtn={activeTab !== li?.name}
                color={AppColors.grayBtn2}
                onClick={() => handleMainTabClick(li?.name)}
              />
            </div>
          ))}
        </div>

        {/* Secondary Tabs - Sub-items for Active Category */}
        {activeGroup && activeGroup?.tabs && activeGroup?.tabs?.length > 1 && (
          <div
            className="flex-row-ali-cen"
            style={{
              flexWrap: "wrap",
              gap: "8px",
              // marginBottom: "16px",
              paddingTop: "8px",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            {activeGroup?.tabs?.map((item, ind) => {
              if (typeof item === "object" && item?.type === "dropdown") {
                const isSelected = item?.options?.some(
                  (opt) => opt.value === selectedTab,
                );
                const selectedOption = item?.options?.find(
                  (opt) => opt.value === selectedTab,
                );
                return (
                  <div
                    key={`${item?.label}_${ind}`}
                    style={{ marginRight: "8px" }}
                  >
                    <MuiButton
                      size="small"
                      variant={isSelected ? "contained" : "outlined"}
                      onClick={(e) => setReturnsReportAnchor(e.currentTarget)}
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{
                        fontSize: 14,
                        borderRadius: "5px",
                        height: 30,
                        textTransform: "capitalize",
                        fontWeight: 600,
                        px: 1.25,
                        fontFamily: "Montserrat",
                        boxShadow: "none",
                        backgroundColor: isSelected
                          ? AppColors.yellowBtn
                          : "transparent",
                        borderColor: isSelected
                          ? AppColors.yellowBtn
                          : AppColors.appPrimary,
                        color: isSelected ? AppColors.white : AppColors.gray3,
                        "&:hover": {
                          boxShadow: "none",
                          backgroundColor: isSelected
                            ? AppColors.yellowBtn
                            : "rgba(0,0,0,0.04)",
                          borderColor: isSelected
                            ? AppColors.yellowBtn
                            : AppColors.appPrimary,
                        },
                      }}
                    >
                      {selectedOption
                        ? `${item?.label} (${selectedOption.label})`
                        : item.label}
                    </MuiButton>
                    <Menu
                      anchorEl={returnsReportAnchor}
                      open={Boolean(returnsReportAnchor)}
                      onClose={() => setReturnsReportAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      PaperProps={{
                        sx: { minWidth: 160, mt: 1.5, boxShadow: "none" },
                      }}
                    >
                      {item.options?.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          onClick={() => {
                            handleSubTabClick(activeTab, opt.value);
                            setReturnsReportAnchor(null);
                          }}
                        >
                          <ListItemText primary={opt?.label} />
                        </MenuItem>
                      ))}
                    </Menu>
                  </div>
                );
              }
              const subTab =
                typeof item === "string" ? item : (item?.label ?? item);
              return (
                <div key={`${subTab}_${ind}`} style={{ marginRight: "8px" }}>
                  <Button
                    text={subTab}
                    smallBtn
                    tabOutlined
                    yellowBtn={selectedTab === subTab}
                    onClick={() => handleSubTabClick(activeTab, subTab)}
                  />
                </div>
              );
            })}
            {selectedTab === "Stock Returns" && (
              <div className="flex1-end">
                <div className="w15Per">
                  <CustomSelect
                    list={[
                      { name: "Sales", value: "Sales" },
                      { name: "Purchase", value: "Purchase" },
                    ]}
                    value={selectedDrop}
                    handleChange={(e) => setSelectedDrop(e?.target?.value)}
                    select
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt10 mh20Min">
          {activeTab === "Executive" ? (
            <ExecutiveOverview />
          ) : null}
          {activeTab === "Vets" ? (
            selectedTab === "Overview" ? (
              <VetsOverview />
            ) : selectedTab === "Vet Performance" ? (
              <VetsRevenueReport />
            ) : selectedTab === "Vet Specialties" ? (
              <VetsSpecialityReport />
            ) : selectedTab === "Utilization vs Capacity" ? (
              <VetsSlotsReport />
            ) : selectedTab === "Upcoming Appointments" ? (
              <VetsUpcomingReport />
            ) : null
          ) : null}
          {activeTab === "Pets" ? (
            selectedTab === "Overview" ? (
              <PetsOverview />
            ) : selectedTab === "Pet Overview" ? (
              <PetDetailsReport />
            ) : selectedTab === "Appointment History" ? (
              <AppointmentsReport />
            ) : null
          ) : null}
          {activeTab === "Appointments" ? (
            selectedTab === "Overview" ? (
              <AppointmentsOverview />
            ) : selectedTab === "Appointment Trends" ? (
              <AppointmentsTableReport />
            ) : selectedTab === "Appointment Analytics" ? (
              <AppointmentsTabTableReport />
            ) : selectedTab === "Facility Utilization" ? (
              <AppointmentsFacilityReport />
            ) : null
          ) : null}
          {activeTab === "Billing" ? (
            selectedTab === "Overview" ? (
              <BillingOverview />
            ) : selectedTab === "Revenue Report" ? (
              <BillingInvoicesTableReport />
            ) : selectedTab === "Collections Report" ? (
              <BillingCollectionsTableReport />
            ) : selectedTab === "Sales Return" ? (
              <BillingSalesReturnReport
                returnType="Sales"
                setReturnType={(type) =>
                  navigateToTab("Billing", type === "Sales" ? "Sales Return" : "Purchase Return")
                }
              />
            ) : selectedTab === "Purchase Return" ? (
              <BillingPurchaseReturnReport
                returnType="Purchase"
                setReturnType={(type) =>
                  navigateToTab("Billing", type === "Sales" ? "Sales Return" : "Purchase Return")
                }
              />
            ) : null
          ) : null}
          {activeTab === "Stock" ? (
            selectedTab === "Overview" ? (
              <StockOverview />
            ) : selectedTab === "Stock Overview" ? (
              <StockDumpReport />
            ) : selectedTab === "Batch Analysis" ? (
              <StockBatchReport />
            ) : selectedTab === "Stock Returns" ? (
              selectedDrop === "Sales" ? (
                <StockSalesReturnReport
                  returnType={selectedDrop}
                  setReturnType={setSelectedDrop}
                />
              ) : selectedDrop === "Purchase" ? (
                <StockPurchaseReturnReport
                  returnType={selectedDrop}
                  setReturnType={setSelectedDrop}
                />
              ) : (
                <StockSalesReturnReport
                  returnType="Sales"
                  setReturnType={setSelectedDrop}
                />
              )
            ) : null
          ) : null}
          {/* selectedTab === "Return" ? (
            <StockReturnReport />
           ) */}
          {activeTab === "Services" ? (
            selectedTab === "Overview" ? (
              <ServicesOverview />
            ) : selectedTab === "Service Analytics" ? (
              <ServiceTableReport />
            ) : selectedTab === "Service Usage" ? (
              <ServiceLevel2Report />
            ) : null
          ) : null}
        </div>
        {/* {selectedTab === "Overview" ? <ReportOverview /> : <DownloadReports />} */}
      </div>
    </Container>
  );
};

export default Reports;
